import * as moment from "moment";
class DateService {
    /**
     * Returns date as formatted date string
     * 'd/m/yy'
     */
    static display(date, locale = "en-US") {
        const options = {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        };
        return DateService.isValidDate(date)
            ? date && new Intl.DateTimeFormat(locale, options).format(date)
            : null;
    }
    /**
     * Returns date as formatted date string
     * 'shortWeekday, d/m/yy'
     */
    static displayLong(date, locale = "en-US") {
        const options = {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            weekday: "short",
        };
        return DateService.isValidDate(date)
            ? date && new Intl.DateTimeFormat(locale, options).format(date)
            : null;
    }
    static getDateWithTime(date, time) {
        const newDate = new Date(date);
        if (!time) {
            return newDate;
        }
        newDate.setHours(time.getHours());
        newDate.setMinutes(time.getMinutes());
        newDate.setSeconds(time.getSeconds());
        newDate.setMilliseconds(time.getMilliseconds());
        return newDate;
    }
    /**
     * Returns date as formatted date string
     * 'h:mm AM/PM'
     */
    static displayJustTime(date, locale = "en-US") {
        const options = {
            hour: "numeric",
            minute: "2-digit",
        };
        return DateService.isValidDate(date)
            ? date && new Intl.DateTimeFormat(locale, options).format(date)
            : null;
    }
    static getStartOfDay(date) {
        // @ts-expect-error - TS2349 - This expression is not callable.
        return moment(date).startOf("day").toDate();
    }
    static parseDateWithSameTimeInLocalTimezone(date) {
        const parsedDate = new Date(date);
        return new Date(parsedDate.getTime() + parsedDate.getTimezoneOffset() * 60000);
    }
    /**
     * Returns date as formatted date string
     * 'shortWeekday, dd/m/yy, h:mm AM/PM'
     */
    static displayWithTime(date, shortWeekday = true, locale = "en-US") {
        const options = {
            year: "2-digit",
            month: "numeric",
            day: "2-digit",
            weekday: shortWeekday ? "short" : "long",
            hour: "numeric",
            minute: "2-digit",
        };
        return DateService.isValidDate(date)
            ? date && new Intl.DateTimeFormat(locale, options).format(date)
            : null;
    }
    /**
     * Returns date as formatted date string
     * 'shortWeekday, d/m/yy, h:mm AM/PM'
     */
    static displayLongWithTime(date, locale = "en-US") {
        const options = {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            weekday: "short",
            hour: "numeric",
            minute: "2-digit",
        };
        return DateService.isValidDate(date)
            ? date && new Intl.DateTimeFormat(locale, options).format(date)
            : null;
    }
    static custom(date, options, locale = "en-US") {
        return DateService.isValidDate(date)
            ? date && new Intl.DateTimeFormat(locale, options).format(date)
            : null;
    }
    static monthAndYear(date, locale = "en-US") {
        const options = {
            year: "numeric",
            month: "long",
        };
        return DateService.isValidDate(date)
            ? date && new Intl.DateTimeFormat(locale, options).format(date)
            : null;
    }
    static getNumWeeksBetween(a, b = new Date()) {
        return Math.floor((b.getTime() - a.getTime()) / 1000 / 60 / 60 / 24 / 7);
    }
    static getHours24(date) {
        return date === null || date === void 0 ? void 0 : date.getHours();
    }
    static getMinutes(date) {
        return date === null || date === void 0 ? void 0 : date.getMinutes();
    }
    /**
     * This method rounds down to the nearest year.  So, it's appropriate for calculating ages in years.
     */
    static getNumYearsBetween(a, b = new Date()) {
        const bDateIsGreaterThanA = b.getMonth() > a.getMonth() ||
            (b.getMonth() === a.getMonth() && b.getDate() > a.getDate());
        const yearDifference = b.getFullYear() - a.getFullYear();
        if (!bDateIsGreaterThanA) {
            return yearDifference - 1;
        }
        return yearDifference;
    }
    static getDaysBetween(a, b = new Date()) {
        return Math.floor((b.getTime() - a.getTime()) / 1000 / 60 / 60 / 24);
    }
    static getHoursBetween(a, b = new Date()) {
        return Math.floor((b.getTime() - a.getTime()) / 1000 / 60 / 60);
    }
    static getMinutesBetween(a, b = new Date()) {
        return Math.floor((b.getTime() - a.getTime()) / 1000 / 60);
    }
    static getSecondsBetween(a, b = new Date()) {
        return Math.floor((b.getTime() - a.getTime()) / 1000);
    }
    static getCompactRelativeTime(date) {
        if (date.getTime() > new Date().getTime()) {
            return "future";
        }
        // We want this to look something like 1y or 2m or 3w or 3h etc etc depending on how long ago it was.
        const years = DateService.getNumYearsBetween(date);
        if (years > 0) {
            return `${years}y`;
        }
        const weeks = DateService.getNumWeeksBetween(date);
        if (weeks) {
            return `${weeks}w`;
        }
        const days = DateService.getDaysBetween(date);
        if (days) {
            return `${days}d`;
        }
        const hours = DateService.getHoursBetween(date);
        if (hours) {
            return `${hours}h`;
        }
        const minutes = DateService.getMinutesBetween(date);
        if (minutes) {
            return `${minutes}m`;
        }
        return `${DateService.getSecondsBetween(date)}s`;
    }
    static getRelativeShortTime(date) {
        // @ts-expect-error - TS2349 - This expression is not callable.
        return moment(date).calendar(null, {
            // @ts-expect-error - TS2349 - This expression is not callable.
            sameDay: `[${moment(date).fromNow(false)}]`,
            nextDay: "[Tomorrow]",
            lastDay: "[Yesterday at] LT",
            lastWeek: "ddd [at] LT",
            sameElse: "ddd M/DD/YYYY LT",
        });
    }
    static getRelativeStringWithoutTime(date) {
        // @ts-expect-error - TS2349 - This expression is not callable.
        return moment(date).calendar(null, {
            sameDay: "[Today]",
            nextDay: "[Tomorrow]",
            nextWeek: "dddd",
            lastDay: "[Yesterday]",
            lastWeek: "dddd",
            sameElse: "M/DD/YYYY",
        });
    }
    static getRelativeString(date) {
        // @ts-expect-error - TS2349 - This expression is not callable.
        return moment(date).calendar();
    }
    static getDurationString(ms) {
        return moment.duration(ms).humanize();
    }
    static isValidDate(date) {
        if (date != null && date instanceof Date && !Number.isNaN(date.getTime())) {
            return true;
        }
        console.error("Date is invalid: " + date);
        return false;
    }
    /**
     * Returns date as formatted date string
     * 'd/m/yy, h:mm AM/PM'
     */
    static displayLongWithTimeWithoutWeekday(date, locale = "en-US") {
        const options = {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        };
        return DateService.isValidDate(date)
            ? date && new Intl.DateTimeFormat(locale, options).format(date)
            : null;
    }
}
DateService.dateTimeToSlashyWithoutTime = (dateToConvert) => {
    if (!dateToConvert) {
        return null;
    }
    const mm = dateToConvert.getMonth() + 1; // getMonth() is zero-based
    const dd = dateToConvert.getDate();
    return [
        (mm > 9 ? "" : "0") + mm,
        (dd > 9 ? "" : "0") + dd,
        dateToConvert.getFullYear() % 100,
    ].join("/");
};
DateService.getDashSeparatedDate = (date) => {
    const relDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
    return relDate.toISOString().split("T")[0];
};
DateService.getNumberOfDaysInMonthFromMMM = (mmm) => {
    const months = {
        Jan: "1",
        Feb: "2",
        Mar: "3",
        Apr: "4",
        May: "5",
        Jun: "6",
        Jul: "7",
        Aug: "8",
        Sep: "09",
        Oct: "10",
        Nov: "11",
        Dec: "12",
    };
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly Jan: "1"; readonly Feb: "2"; readonly Mar: "3"; readonly Apr: "4"; readonly May: "5"; readonly Jun: "6"; readonly Jul: "7"; readonly Aug: "8"; readonly Sep: "09"; readonly Oct: "10"; readonly Nov: "11"; readonly Dec: "12"; }'.
    const thisMonth = months[mmm];
    const today = new Date();
    today.setMonth(thisMonth + 1);
    today.setDate(0);
    return today.getDate();
};
DateService.INVALID_DATE_MESSAGE = null;
export default DateService;
